import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight, ArrowLeft, CircleNotch, FilePlus, Sparkle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { proposalService } from '../../../services/proposal.service';
import type { Proposal, ProposalInput, ProposalMilestone, ProposalOptions } from '../../../services/proposal.service';
import { DocumentDropZone } from './DocumentDropZone';
import { storedSpelling } from '../personNames';

const PEOPLE_LIST = 'proposal-people';

interface ProposalFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  proposal?: Proposal | null;
  onClose: () => void;
  onSaved: (p: Proposal) => void;
}

type FormState = {
  title: string;
  domain: string;
  sub_domain: string;
  pi: string;
  co_pis: string[];
  department: string;
  budget_lakhs: string;
  duration_months: string;
  description: string;
  un_sdg: string;
  keywords: string[];
  // Project Highlights; milestones are edited as lines ("Months 0-6: text").
  problem: string;
  solution: string;
  impact: string;
  milestones: string;
  categorization: string;
  proposal_period: string;
  project_type: string;
  document_nature: string;
  posters_available: string;
};

type FieldKey = keyof FormState;

const EMPTY: FormState = {
  title: '', domain: '', sub_domain: '', pi: '', co_pis: [], department: '', budget_lakhs: '',
  duration_months: '', description: '', un_sdg: '', keywords: [], problem: '', solution: '', impact: '',
  milestones: '', categorization: '', proposal_period: '', project_type: '', document_nature: '',
  posters_available: '',
};

const milestonesToLines = (ms?: ProposalMilestone[] | null) =>
  (ms || []).map((m) => (m.period ? `${m.period}: ${m.text}` : m.text)).join('\n');

/** "Months 0-6: Surveys" → period + text. A colon later than 25 characters in is part of the text. */
const linesToMilestones = (s: string): ProposalMilestone[] =>
  s.split('\n')
    .map((line) => line.replace(/^\s*[•*-]\s*/, '').trim())
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf(':');
      return i > 0 && i <= 25
        ? { period: line.slice(0, i).trim(), text: line.slice(i + 1).trim() }
        : { period: '', text: line };
    });

const HIGHLIGHT_TEXTS = [
  { key: 'problem', label: 'Problem' },
  { key: 'solution', label: 'Solution' },
  { key: 'impact', label: 'Impact' },
] as const;

const fromProposal = (p: Proposal): FormState => ({
  title: p.title || '',
  domain: p.domain || '',
  sub_domain: p.sub_domain || '',
  pi: p.pi || '',
  co_pis: p.co_pis || [],
  department: p.department || '',
  budget_lakhs: p.budget_lakhs != null ? String(p.budget_lakhs) : '',
  duration_months: p.duration_months != null ? String(p.duration_months) : '',
  description: p.description || '',
  un_sdg: p.un_sdg || '',
  keywords: p.keywords || [],
  problem: p.highlights?.problem || '',
  solution: p.highlights?.solution || '',
  impact: p.highlights?.impact || '',
  milestones: milestonesToLines(p.highlights?.milestones),
  categorization: p.categorization || '',
  proposal_period: p.proposal_period || '',
  project_type: p.project_type || '',
  document_nature: p.document_nature || '',
  posters_available: p.posters_available || '',
});

const toInput = (f: FormState): ProposalInput => ({
  title: f.title.trim(),
  domain: f.domain.trim(),
  sub_domain: f.sub_domain.trim(),
  pi: f.pi.trim(),
  co_pis: f.co_pis,
  department: f.department,
  budget_lakhs: f.budget_lakhs.trim() === '' ? null : Number(f.budget_lakhs),
  duration_months: f.duration_months.trim() === '' ? null : Number(f.duration_months),
  description: f.description.trim(),
  un_sdg: f.un_sdg || null,
  keywords: f.keywords,
  highlights: {
    problem: f.problem.trim(),
    solution: f.solution.trim(),
    impact: f.impact.trim(),
    milestones: linesToMilestones(f.milestones),
  },
  categorization: f.categorization || null,
  proposal_period: f.proposal_period.trim() || null,
  project_type: f.project_type || null,
  document_nature: f.document_nature || null,
  posters_available: f.posters_available || null,
});

const LABELS: Record<string, string> = {
  title: 'Proposal Title', domain: 'Domain', sub_domain: 'Sub-domain', pi: 'Principal Investigator',
  department: 'Department', budget_lakhs: 'Budget', description: 'Summary', co_pis: 'Co-PI(s)',
  duration_months: 'Duration', un_sdg: 'UN SDG', keywords: 'Keywords', problem: 'Problem',
  solution: 'Solution', impact: 'Impact', milestones: 'Milestones',
};

/** What the AI filled in: the server compares it with what is saved and learns from the changes. */
type AiAnswer = { form: FormState; filled: FieldKey[]; input: ProposalInput; text: string };

const sameValue = (a: FormState[FieldKey], b: FormState[FieldKey]) =>
  Array.isArray(a) && Array.isArray(b)
    ? a.map((s) => s.trim().toLowerCase()).sort().join('\n') === b.map((s) => s.trim().toLowerCase()).sort().join('\n')
    : String(a).trim() === String(b).trim();

const inputCls =
  'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all disabled:bg-slate-50';

const CUSTOM = '__custom__';

function Label({ text, required, ai, isNew }: { text: string; required?: boolean; ai?: boolean; isNew?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
        {text} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="flex gap-1">
        {isNew && (
          <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            New — not in list
          </span>
        )}
        {ai && (
          <span className="text-[9px] font-semibold text-accent bg-accent/5 border border-accent/20 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Sparkle size={9} weight="fill" /> AI
          </span>
        )}
      </div>
    </div>
  );
}

/** Dropdown with an optional "Add custom…" escape hatch that turns into a text box. */
function Pick({
  value, options, onChange, allowCustom, placeholder, priority, disabled,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  allowCustom?: boolean;
  placeholder: string;
  priority?: string[];
  disabled?: boolean;
}) {
  const known = useMemo(() => new Set([...(priority || []), ...options]), [options, priority]);
  const [custom, setCustom] = useState(false);
  const isCustom = custom || (!!value && !known.has(value) && !!allowCustom);

  if (isCustom) {
    return (
      <div className="flex gap-2">
        <input
          autoFocus={custom}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type a new value"
          disabled={disabled}
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => { setCustom(false); onChange(''); }}
          disabled={disabled}
          className="px-3 rounded-xl border border-slate-200 text-[10px] font-bold text-slate-500 hover:bg-slate-50 shrink-0"
        >
          List
        </button>
      </div>
    );
  }

  const rest = options.filter((o) => !(priority || []).includes(o));
  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === CUSTOM) { setCustom(true); onChange(''); } else onChange(e.target.value);
      }}
      disabled={disabled}
      className={inputCls}
    >
      <option value="">{placeholder}</option>
      {/* A stored value outside the list (e.g. an imported department) stays selectable. */}
      {value && !known.has(value) && <option value={value}>{value}</option>}
      {priority && priority.length > 0 ? (
        <>
          <optgroup label="Used in this domain">
            {priority.map((o) => <option key={`p-${o}`} value={o}>{o}</option>)}
          </optgroup>
          <optgroup label="All">
            {rest.map((o) => <option key={o} value={o}>{o}</option>)}
          </optgroup>
        </>
      ) : (
        options.map((o) => <option key={o} value={o}>{o}</option>)
      )}
      {allowCustom && <option value={CUSTOM}>+ Add custom…</option>}
    </select>
  );
}

/** "Stored as S. Pushpavanam — use that" under a name typed in a different spelling. */
function SamePersonHint({ name, people, onUse }: { name: string; people: string[]; onUse: (stored: string) => void }) {
  const stored = storedSpelling(name, people);
  if (!stored) return null;
  return (
    <p className="text-[10px] font-semibold text-amber-700">
      "{name}" is already stored as "{stored}".{' '}
      <button type="button" onClick={() => onUse(stored)} className="underline">Use that</button>
    </p>
  );
}

function Chips({ values, onChange, placeholder, disabled, listId }: {
  values: string[]; onChange: (v: string[]) => void; placeholder: string; disabled?: boolean; listId?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !values.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-2 rounded-xl border border-slate-200 focus-within:border-accent focus-within:ring-1 focus-within:ring-accent">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className="text-[10px] font-bold bg-slate-100 border border-slate-200 text-slate-600 pl-2 pr-1 py-0.5 rounded-lg flex items-center gap-1">
          {v}
          <button type="button" disabled={disabled} onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500">
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={add}
        list={listId}
        disabled={disabled}
        placeholder={values.length ? '' : placeholder}
        className="flex-1 min-w-[120px] text-xs font-medium text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
      />
    </div>
  );
}

/** Error detail from apiClient's ApiError (or anything thrown). */
const errInfo = (err: unknown): { message?: string; missing?: string[] } => {
  const e = err as { message?: string; data?: { detail?: { missing?: string[] } } } | null;
  return { message: e?.message, missing: e?.data?.detail?.missing };
};

/** Mount it only while open (the parent renders it conditionally), so each open starts clean. */
export function ProposalFormModal({ isOpen, mode, proposal, onClose, onSaved }: ProposalFormModalProps) {
  const start = mode === 'edit' && proposal ? fromProposal(proposal) : EMPTY;
  const [step, setStep] = useState<1 | 2>(mode === 'edit' ? 2 : 1);
  const [options, setOptions] = useState<ProposalOptions | null>(null);
  const [form, setForm] = useState<FormState>(start);
  const [original] = useState<FormState>(start);
  const [files, setFiles] = useState<File[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiFields, setAiFields] = useState<Set<FieldKey>>(new Set());
  const [newFlags, setNewFlags] = useState({ domain: false, sub_domain: false });
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiAnswer, setAiAnswer] = useState<AiAnswer | null>(null);

  useEffect(() => {
    proposalService.getOptions().then((o) => {
      setOptions(o);
      if (mode === 'create') setForm((f) => ({ ...f, proposal_period: f.proposal_period || o.default_period }));
    }).catch(() => toast.error('Could not load the field lists.'));
  }, [mode]);

  if (!isOpen) return null;

  const set = <K extends FieldKey>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Once the user changes a field it is theirs, not the AI's: drop the badges.
    setAiFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    if (key === 'domain' || key === 'sub_domain') setNewFlags((n) => ({ ...n, [key]: false }));
  };

  const people = options?.people || [];
  const required = (options?.required || ['title', 'domain', 'sub_domain', 'pi', 'department', 'budget_lakhs', 'description']) as FieldKey[];
  const missing = required.filter((k) => {
    const v = form[k];
    return Array.isArray(v) ? v.length === 0 : String(v).trim() === '';
  });

  const analyze = async () => {
    if (files.length === 0) { setStep(2); return; }
    setAnalyzing(true);
    try {
      const res = await proposalService.extract(files);
      const s = res.suggested;
      const filled = new Set<FieldKey>();
      const next: FormState = { ...form };
      const take = <K extends FieldKey>(k: K, v: FormState[K] | null | undefined) => {
        const empty = v == null || (Array.isArray(v) ? v.length === 0 : String(v) === '');
        if (!empty) { next[k] = v as FormState[K]; filled.add(k); }
      };
      take('title', s.title);
      take('domain', s.domain);
      take('sub_domain', s.sub_domain);
      take('pi', s.pi);
      take('co_pis', s.co_pis);
      take('department', s.department);
      take('budget_lakhs', s.budget_lakhs != null ? String(s.budget_lakhs) : '');
      take('duration_months', s.duration_months != null ? String(s.duration_months) : '');
      take('description', s.description);
      take('un_sdg', s.un_sdg);
      take('keywords', s.keywords);
      take('problem', s.highlights?.problem);
      take('solution', s.highlights?.solution);
      take('impact', s.highlights?.impact);
      take('milestones', milestonesToLines(s.highlights?.milestones));
      if (res.document_nature && !next.document_nature) next.document_nature = res.document_nature;
      setForm(next);
      setAiFields(filled);
      // Only the fields the AI filled: anything else in the form was the reviewer's.
      const onlyAi = { ...EMPTY };
      filled.forEach((k) => { (onlyAi as Record<FieldKey, unknown>)[k] = next[k]; });
      setAiAnswer(filled.size ? { form: next, filled: [...filled], input: toInput(onlyAi), text: res.document_text || '' } : null);
      setNewFlags({ domain: s.domain_is_new, sub_domain: s.sub_domain_is_new });
      setAiError(res.error);
      if (res.error) toast.warning(res.error); else toast.success('Filled from the document — please review.');
      setStep(2);
    } catch (err) {
      toast.error(errInfo(err).message || 'Document analysis failed. You can fill the form manually.');
      setStep(2);
    } finally {
      setAnalyzing(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((k) => LABELS[k] || k).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (mode === 'create') {
        saved = await proposalService.create(aiAnswer
          ? { ...toInput(form), ai_proposed: aiAnswer.input, document_text: aiAnswer.text }
          : toInput(form));
        const pid = saved.proposal.proposal_id as string;
        if (files.length) {
          try {
            await proposalService.uploadDocuments(pid, files);
          } catch {
            toast.warning(`${pid} saved, but the document upload to storage failed.`);
          }
        }
        toast.success(`Saved as ${pid}`);
      } else {
        // Send only what changed, so untouched imported values are left exactly as they are.
        const now = toInput(form);
        const before = toInput(original);
        const changed = Object.fromEntries(
          Object.entries(now).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(before[k as keyof ProposalInput]))
        ) as ProposalInput;
        saved = await proposalService.update(proposal!.proposal_id as string, changed);
        toast.success(`${proposal!.proposal_id} updated`);
      }
      if (saved.index_pending) toast.warning('Saved, but the search index could not be updated yet.');
      onSaved(saved.proposal);
      onClose();
    } catch (err) {
      const { message, missing: miss } = errInfo(err);
      toast.error(miss ? `Please fill: ${miss.map((k) => LABELS[k] || k).join(', ')}` : message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const o = options;
  const busy = analyzing || saving;
  const corrected = mode === 'create' && aiAnswer
    ? aiAnswer.filled.filter((k) => !sameValue(form[k], aiAnswer.form[k]))
    : [];
  const isReq = (k: FieldKey) => required.includes(k);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white border border-slate-200/80 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
              <FilePlus className="w-4 h-4" weight="fill" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {mode === 'edit' ? `Edit ${proposal?.proposal_id}` : step === 1 ? 'New Proposal' : 'Proposal Details'}
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">
                {step === 1 ? 'Upload the proposal document — AI fills the form once' : 'Review and edit before saving'}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-full hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 1 ? (
          <div className="p-6 space-y-3 overflow-y-auto flex-1">
            <DocumentDropZone files={files} onChange={setFiles} disabled={analyzing} />
          </div>
        ) : (
          <form id="proposal-form" onSubmit={save} className="p-6 space-y-4 overflow-y-auto flex-1">
            {aiError && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <WarningCircle size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[10px] font-semibold text-amber-700 leading-snug">{aiError}</p>
              </div>
            )}

            <div className="space-y-1">
              <Label text="Proposal Title" required={isReq('title')} ai={aiFields.has('title')} />
              <input value={form.title} onChange={(e) => set('title', e.target.value)} disabled={saving} className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label text="Domain" required={isReq('domain')} ai={aiFields.has('domain')} isNew={newFlags.domain} />
                <Pick value={form.domain} options={o?.domains || []} onChange={(v) => set('domain', v)} allowCustom placeholder="Select domain" disabled={saving} />
              </div>
              <div className="space-y-1">
                <Label text="Sub-domain" required={isReq('sub_domain')} ai={aiFields.has('sub_domain')} isNew={newFlags.sub_domain} />
                <Pick value={form.sub_domain} options={o?.sub_domains || []} priority={o?.sub_domains_by_domain[form.domain]}
                  onChange={(v) => set('sub_domain', v)} allowCustom placeholder="Select sub-domain" disabled={saving} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label text="Principal Investigator" required={isReq('pi')} ai={aiFields.has('pi')} />
                <input value={form.pi} onChange={(e) => set('pi', e.target.value)} list={PEOPLE_LIST}
                  placeholder="Start typing to pick an existing name" disabled={saving} className={inputCls} />
                <SamePersonHint name={form.pi} people={people} onUse={(n) => set('pi', n)} />
              </div>
              <div className="space-y-1">
                <Label text="Co-PI(s)" ai={aiFields.has('co_pis')} />
                <Chips values={form.co_pis} onChange={(v) => set('co_pis', v)} placeholder="Type a name, press Enter"
                  disabled={saving} listId={PEOPLE_LIST} />
                {form.co_pis.map((n, i) => (
                  <SamePersonHint key={`${n}-${i}`} name={n} people={people}
                    onUse={(stored) => set('co_pis', form.co_pis.map((x, j) => (j === i ? stored : x))
                      .filter((x, j, all) => all.indexOf(x) === j))} />
                ))}
                {/* One suggestion list for both boxes: every PI / Co-PI already stored, most used first. */}
                <datalist id={PEOPLE_LIST}>
                  {people.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1 sm:col-span-1">
                <Label text="Department" required={isReq('department')} ai={aiFields.has('department')} />
                <Pick value={form.department} options={o?.departments || []} onChange={(v) => set('department', v)} placeholder="Select" disabled={saving} />
              </div>
              <div className="space-y-1">
                <Label text="Budget (Lakhs)" required={isReq('budget_lakhs')} ai={aiFields.has('budget_lakhs')} />
                <input type="number" min="0" step="any" value={form.budget_lakhs} onChange={(e) => set('budget_lakhs', e.target.value)}
                  placeholder={proposal?.budget_note || ''} disabled={saving} className={inputCls} />
              </div>
              <div className="space-y-1">
                <Label text="Duration (months)" ai={aiFields.has('duration_months')} />
                <input type="number" min="0" step="1" value={form.duration_months} onChange={(e) => set('duration_months', e.target.value)} disabled={saving} className={inputCls} />
              </div>
            </div>

            <div className="space-y-1">
              <Label text="Summary" required={isReq('description')} ai={aiFields.has('description')} />
              <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} disabled={saving} className={`${inputCls} resize-none`} />
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-slate-50/40 p-4">
              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Project Highlights</p>
              {HIGHLIGHT_TEXTS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label text={label} ai={aiFields.has(key)} />
                  <textarea value={form[key]} onChange={(e) => set(key, e.target.value)} rows={2} disabled={saving} className={`${inputCls} resize-none bg-white`} />
                </div>
              ))}
              <div className="space-y-1">
                <Label text="Major Milestones" ai={aiFields.has('milestones')} />
                <textarea value={form.milestones} onChange={(e) => set('milestones', e.target.value)} rows={4} disabled={saving}
                  placeholder={'Months 0-6: Field surveys and design\nMonths 7-12: Prototype and field testing'}
                  className={`${inputCls} resize-none bg-white`} />
                <p className="text-[10px] text-slate-400 font-medium">
                  One milestone per line. The Budget line is generated from Budget and Duration.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label text="UN SDG" ai={aiFields.has('un_sdg')} />
                <Pick value={form.un_sdg} options={o?.sdgs || []} onChange={(v) => set('un_sdg', v)} placeholder="Select goal" disabled={saving} />
              </div>
              <div className="space-y-1">
                <Label text="Keywords / Tags" ai={aiFields.has('keywords')} />
                <Chips values={form.keywords} onChange={(v) => set('keywords', v)} placeholder="Type a keyword, press Enter" disabled={saving} />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Optional details</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label text="Categorization" />
                  <Pick value={form.categorization} options={o?.categorizations || []} onChange={(v) => set('categorization', v)} placeholder="—" disabled={saving} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label text="Proposal Period" />
                  <Pick value={form.proposal_period} options={o?.periods || []} onChange={(v) => set('proposal_period', v)} allowCustom placeholder="—" disabled={saving} />
                </div>
                <div className="space-y-1">
                  <Label text="Type of Project" />
                  <Pick value={form.project_type} options={o?.project_types || []} onChange={(v) => set('project_type', v)} placeholder="—" disabled={saving} />
                </div>
                <div className="space-y-1">
                  <Label text="Nature of Document" />
                  <Pick value={form.document_nature} options={o?.doc_natures || []} onChange={(v) => set('document_nature', v)} placeholder="—" disabled={saving} />
                </div>
                <div className="space-y-1">
                  <Label text="Posters Available" />
                  <Pick value={form.posters_available} options={o?.posters || []} onChange={(v) => set('posters_available', v)} placeholder="—" disabled={saving} />
                </div>
              </div>
            </div>
          </form>
        )}

        {step === 2 && corrected.length > 0 && (
          <div className="px-6 pb-3 shrink-0">
            <div className="flex items-start gap-2.5 bg-accent/5 border border-accent/20 rounded-xl p-3">
              <Sparkle className="w-4 h-4 text-accent shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="text-[11px] font-bold text-accent leading-tight">Correct AI prompt</p>
                <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">
                  You changed {corrected.length} field{corrected.length > 1 ? 's' : ''} the AI filled in
                  ({corrected.map((k) => LABELS[k] || k).join(', ')}). When you save, the AI learns from your corrections.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          {step === 1 ? (
            <>
              <button type="button" onClick={onClose} disabled={analyzing}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all">
                Cancel
              </button>
              <button type="button" onClick={analyze} disabled={analyzing}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all">
                {analyzing ? (<><CircleNotch className="w-4 h-4 animate-spin" /> Reading document…</>)
                  : (<>{files.length ? 'Analyse with AI' : 'Skip — fill manually'} <ArrowRight className="w-4 h-4" weight="bold" /></>)}
              </button>
            </>
          ) : (
            <>
              {mode === 'create' ? (
                <button type="button" onClick={() => setStep(1)} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all">
                  <ArrowLeft className="w-4 h-4" weight="bold" /> Back
                </button>
              ) : (
                <button type="button" onClick={onClose} disabled={saving}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all">
                  Cancel
                </button>
              )}
              <div className="flex items-center gap-3">
                {missing.length > 0 && (
                  <span className="text-[10px] font-semibold text-slate-400">{missing.length} required field{missing.length > 1 ? 's' : ''} left</span>
                )}
                <button type="submit" form="proposal-form" disabled={saving || missing.length > 0}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all">
                  {saving ? (<><CircleNotch className="w-4 h-4 animate-spin" /> Saving…</>)
                    : (<><FilePlus className="w-4 h-4" weight="fill" /> {mode === 'create' ? 'Save Proposal' : 'Save Changes'}</>)}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
